import { useQuery, useMutation } from 'convex/react';
import { api } from '../../api';
import { useToast } from '@/hooks/use-toast';
import { type NsfwPresetLevel, type AccuracyLevel, DEFAULT_BLUEJAY_CONFIG } from '@/config/nsfwRulePresets';
import { CATEGORY_HIERARCHY } from '@/config/categoryHierarchy';

// Default blocked categories (from memory: 14 specific categories)
const DEFAULT_BLOCKED_CATEGORIES = [
  "Women's Personal Apparel",
  "Gaming",
  "Movies and TV",
  "Music and Audio Streaming",
  "Video Streaming Platforms",
  "News (Non-Jewish)",
  "Sports",
  "Style & Fashion",
  "Celebrity",
  "Gambling",
  "Drugs",
  "Alcohol & Tobacco",
  "Piracy & Illegal Downloads",
  "Defacement",
  "Violence"
];

export interface LevelBrowserPolicy {
  _id: string;
  _creationTime: number;
  levelId: string;
  categories: string[];
  allowlist?: string[];
  blocklist?: string[];
  // BlueJay config
  nsfwLevel?: NsfwPresetLevel;
  human?: boolean;
  accuracy?: AccuracyLevel;
}

export const useLevelBrowserPolicies = (levelId: string) => {
  const { toast } = useToast();
  
  // Get the actual level data that contains browser policy information
  const levelData = useQuery(api.levels.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100
    }
  });

  const updateLevelBrowserMutation = useMutation(api.levels.policies.update);

  const updateBrowserPolicy = async (data: { 
    categories?: string[]; 
    allowlist?: string[]; 
    blocklist?: string[]; 
    nsfwLevel?: NsfwPresetLevel;
    human?: boolean;
    accuracy?: AccuracyLevel;
  }) => {
    try {
      // Find the level object by Convex ID
      const currentLevel = levelData?.page?.find(level => level._id === levelId);
      if (!currentLevel) {
        throw new Error("Level not found");
      }

      // Build blueJayRules with categories, allowlist, blocklist at top level and config nested
      // Handle case where blueJayRules might be false (browser disabled)
      const existingRules = typeof currentLevel.blueJayRules === 'object' && currentLevel.blueJayRules !== null 
        ? currentLevel.blueJayRules 
        : null;
      const finalCategories = data.categories ?? existingRules?.categories ?? currentLevel.policyCategories ?? [];
      const finalAllowlist = data.allowlist ?? existingRules?.allowlist ?? currentLevel.allowedUrls ?? [];
      const finalBlocklist = data.blocklist ?? existingRules?.blocklist ?? currentLevel.blockedUrls ?? [];
      const finalNsfwLevel = data.nsfwLevel ?? existingRules?.config?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel;
      const finalHuman = data.human ?? existingRules?.config?.human ?? DEFAULT_BLUEJAY_CONFIG.human;
      const finalAccuracy = data.accuracy ?? existingRules?.config?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy;

      const blueJayRules = {
        categories: finalCategories,
        allowlist: finalAllowlist,
        blocklist: finalBlocklist,
        config: {
          nsfwLevel: finalNsfwLevel,
          human: finalHuman,
          accuracy: finalAccuracy,
        },
      };

      await updateLevelBrowserMutation({
        level: currentLevel._id as any,
        policyCategories: data.categories || currentLevel.policyCategories || [],
        allowlist: data.allowlist,
        blocklist: data.blocklist,
        blueJayRules
      });
      
      toast({
        title: "Success",
        description: "Browser policy updated successfully",
      });
    } catch (error) {
      console.error('Error updating browser policy:', error);
      toast({
        title: "Error",
        description: "Failed to update browser policy",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Set whitelist mode - block all categories
  const setWhitelistMode = async (
    allCategoryNames: string[],
    config: { nsfwLevel: NsfwPresetLevel; human: boolean; accuracy: AccuracyLevel },
    currentAllowlist: string[]
  ) => {
    try {
      const currentLevel = levelData?.page?.find(level => level._id === levelId);
      if (!currentLevel) {
        throw new Error("Level not found");
      }

      const existingRules = typeof currentLevel.blueJayRules === 'object' && currentLevel.blueJayRules !== null 
        ? currentLevel.blueJayRules 
        : null;

      const blueJayRules = {
        categories: allCategoryNames, // Block ALL categories
        allowlist: currentAllowlist,
        blocklist: existingRules?.blocklist ?? [],
        config: {
          nsfwLevel: config.nsfwLevel,
          human: config.human,
          accuracy: config.accuracy,
        },
      };

      await updateLevelBrowserMutation({
        level: currentLevel._id as any,
        policyCategories: allCategoryNames,
        allowlist: currentAllowlist,
        blueJayRules
      });

      toast({
        title: "Success",
        description: "Browser set to whitelist mode",
      });
    } catch (error) {
      console.error('Error setting whitelist mode:', error);
      toast({
        title: "Error",
        description: "Failed to set whitelist mode",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Set filtered mode - use default blocked categories
  const setFilteredMode = async (
    config: { nsfwLevel: NsfwPresetLevel; human: boolean; accuracy: AccuracyLevel },
    currentLists: { allowlist: string[]; blocklist: string[] }
  ) => {
    try {
      const currentLevel = levelData?.page?.find(level => level._id === levelId);
      if (!currentLevel) {
        throw new Error("Level not found");
      }

      const blueJayRules = {
        categories: DEFAULT_BLOCKED_CATEGORIES, // Use default blocked categories
        allowlist: currentLists.allowlist,
        blocklist: currentLists.blocklist,
        config: {
          nsfwLevel: config.nsfwLevel,
          human: config.human,
          accuracy: config.accuracy,
        },
      };

      await updateLevelBrowserMutation({
        level: currentLevel._id as any,
        policyCategories: DEFAULT_BLOCKED_CATEGORIES,
        allowlist: currentLists.allowlist,
        blocklist: currentLists.blocklist,
        blueJayRules
      });

      toast({
        title: "Success",
        description: "Browser set to filtered mode",
      });
    } catch (error) {
      console.error('Error setting filtered mode:', error);
      toast({
        title: "Error",
        description: "Failed to set filtered mode",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Disable browser completely
  const disableBrowser = async () => {
    try {
      const currentLevel = levelData?.page?.find(level => level._id === levelId);
      if (!currentLevel) {
        throw new Error("Level not found");
      }

      await updateLevelBrowserMutation({
        level: currentLevel._id as any,
        policyCategories: [],
        blueJayRules: false as any
      });

      toast({
        title: "Success",
        description: "Browser disabled for this level",
      });
    } catch (error) {
      console.error('Error disabling browser:', error);
      toast({
        title: "Error",
        description: "Failed to disable browser",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Find the specific level from the list
  const currentLevel = levelData?.page?.find(level => level._id === levelId);

  // Handle case where blueJayRules might be false (browser disabled)
  const currentRules = typeof currentLevel?.blueJayRules === 'object' && currentLevel.blueJayRules !== null 
    ? currentLevel.blueJayRules 
    : null;

  return {
    browserPolicy: currentLevel ? {
      _id: currentLevel._id,
      _creationTime: Date.now(),
      levelId: currentLevel._id,
      // Read from blueJayRules structure first, fallback to legacy fields
      categories: currentRules?.categories ?? currentLevel.policyCategories ?? [],
      allowlist: currentRules?.allowlist ?? currentLevel.allowedUrls ?? [],
      blocklist: currentRules?.blocklist ?? currentLevel.blockedUrls ?? [],
      // BlueJay config - read from nested config object
      nsfwLevel: currentRules?.config?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel,
      human: currentRules?.config?.human ?? DEFAULT_BLUEJAY_CONFIG.human,
      accuracy: currentRules?.config?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy,
    } : null,
    blueJayRules: currentLevel?.blueJayRules,
    isLoading: levelData === undefined,
    updateBrowserPolicy,
    setWhitelistMode,
    setFilteredMode,
    disableBrowser
  };
};
